import { apiClient } from './apiClient';
import type {
  PlanningWorkItemEvidence,
  PlanningWorkItemEvidenceFormPayload,
} from './strategicPlanningApi';
import type {
  TasksCalendarCreateContext,
  TasksCalendarCreatePayload,
  TasksCalendarEvent,
  TasksCalendarTaskListItem,
  TasksCalendarTaskListResult,
  TasksCalendarUser,
} from './tasksCalendarApi';
import { cachedRequest, invalidateRequestCache } from '../utils/requestCache';

const CACHE_PREFIX = 'my-tasks:';

export type TasksScope = {
  profileMode: 'executive' | 'manager' | 'collaborator' | string;
  canSearchCompanyUsers: boolean;
  defaultUsers: TasksCalendarUser[];
};

export type TaskEvaluation = {
  id: string;
  result: string;
  quality: string;
  deadline: string;
  impact: string;
  lessonsLearned?: string | null;
  notes?: string | null;
  evaluatedByUserId?: string | null;
  evaluatedAt: string;
};

export type MyTaskListItem = TasksCalendarTaskListItem & {
  kind?: string;
  priority?: string;
  fulfillmentResult?: string | null;
  canFinish?: boolean;
  canExtendDeadline?: boolean;
  canEvaluate?: boolean;
  isEvaluated?: boolean;
  evaluation?: TaskEvaluation | null;
};

export type TaskParentLinkOption = {
  id: string;
  kind: string;
  title: string;
  objectiveId: string;
  objectiveTitle: string;
  cycleId: string;
  cycleName: string;
};

export type MyTaskListResult = Omit<TasksCalendarTaskListResult, 'items'> & {
  items: MyTaskListItem[];
};

export type TaskEvaluationPayload = {
  result: string;
  quality: string;
  deadline: string;
  impact: string;
  lessonsLearned?: string;
  notes?: string;
};

function invalidateMyTasksCache(): void {
  invalidateRequestCache(CACHE_PREFIX);
}

export async function fetchMyTasksScope(): Promise<TasksScope> {
  return cachedRequest(`${CACHE_PREFIX}scope`, async () => {
    const response = await apiClient.get<TasksScope>('/v1/me/tasks/scope');
    return response.data;
  });
}

export async function searchMyTasksUsers(query: string): Promise<TasksCalendarUser[]> {
  const term = query.trim();
  return cachedRequest(`${CACHE_PREFIX}users-search:${term.toLowerCase()}`, async () => {
    const response = await apiClient.get<TasksCalendarUser[]>('/v1/me/tasks/users/search', {
      params: { q: term },
    });
    return response.data;
  });
}

export async function listMyTasks(params: {
  from: string;
  to: string;
  userIds?: string[];
  status?: string;
}): Promise<MyTaskListResult> {
  const userKey = (params.userIds ?? []).slice().sort().join(',');
  const cacheKey = `${CACHE_PREFIX}list:${params.from}:${params.to}:${userKey}:${params.status ?? ''}`;

  return cachedRequest(cacheKey, async () => {
    const response = await apiClient.get<MyTaskListResult>('/v1/me/tasks', {
      params: {
        from: params.from,
        to: params.to,
        userIds: params.userIds,
        status: params.status || undefined,
      },
      paramsSerializer: {
        indexes: null,
      },
    });
    return response.data;
  });
}

export async function listMyTaskEvents(params: {
  from: string;
  to: string;
  userIds?: string[];
}): Promise<TasksCalendarEvent[]> {
  const userKey = (params.userIds ?? []).slice().sort().join(',');
  const cacheKey = `${CACHE_PREFIX}events:${params.from}:${params.to}:${userKey}`;

  return cachedRequest(cacheKey, async () => {
    const response = await apiClient.get<TasksCalendarEvent[]>('/v1/me/tasks/events', {
      params: {
        from: params.from,
        to: params.to,
        userIds: params.userIds,
      },
      paramsSerializer: {
        indexes: null,
      },
    });
    return response.data;
  });
}

export async function fetchMyTaskCreateContext(
  ownerUserId: string,
  taskStartDate?: string,
): Promise<TasksCalendarCreateContext> {
  const cacheKey = `${CACHE_PREFIX}create-context:${ownerUserId}:${taskStartDate ?? ''}`;
  return cachedRequest(cacheKey, async () => {
    const response = await apiClient.get<TasksCalendarCreateContext>('/v1/me/tasks/create-context', {
      params: { ownerUserId, taskStartDate },
    });
    return response.data;
  });
}

export async function createMyTask(payload: TasksCalendarCreatePayload): Promise<void> {
  await apiClient.post('/v1/me/tasks', payload);
  invalidateMyTasksCache();
}

export async function startMyTask(taskId: string): Promise<void> {
  await apiClient.post(`/v1/me/tasks/${taskId}/start`);
  invalidateMyTasksCache();
}

export async function pauseMyTask(taskId: string): Promise<void> {
  await apiClient.post(`/v1/me/tasks/${taskId}/pause`);
  invalidateMyTasksCache();
}

export async function finishMyTask(taskId: string, fulfillmentResult: string): Promise<void> {
  await apiClient.post(`/v1/me/tasks/${taskId}/finish`, { fulfillmentResult });
  invalidateMyTasksCache();
}

export async function extendMyTaskDeadline(
  taskId: string,
  payload: { newPlannedEndDate: string; reason: string },
): Promise<void> {
  await apiClient.post(`/v1/me/tasks/${taskId}/extend-deadline`, payload);
  invalidateMyTasksCache();
}

export async function evaluateMyTask(
  taskId: string,
  payload: TaskEvaluationPayload,
): Promise<TaskEvaluation> {
  const response = await apiClient.post<TaskEvaluation>(`/v1/me/tasks/${taskId}/evaluate`, payload);
  invalidateMyTasksCache();
  return response.data;
}

export async function cancelMyTask(taskId: string, cancellationReason: string): Promise<void> {
  await apiClient.post(`/v1/me/tasks/${taskId}/cancel`, { cancellationReason });
  invalidateMyTasksCache();
}

export async function listMyTasksInventory(params: {
  from?: string;
  to?: string;
  status?: string;
  unassignedOnly?: boolean;
  withoutParentOnly?: boolean;
}): Promise<MyTaskListResult> {
  const cacheKey = [
    CACHE_PREFIX,
    'inventory',
    params.from ?? '',
    params.to ?? '',
    params.status ?? '',
    params.unassignedOnly ? '1' : '0',
    params.withoutParentOnly ? '1' : '0',
  ].join(':');

  return cachedRequest(cacheKey, async () => {
    const response = await apiClient.get<MyTaskListResult>('/v1/me/tasks/inventory', {
      params: {
        from: params.from || undefined,
        to: params.to || undefined,
        status: params.status || undefined,
        unassignedOnly: params.unassignedOnly || undefined,
        withoutParentOnly: params.withoutParentOnly || undefined,
      },
    });
    return response.data;
  });
}

export async function listMyTaskParentOptions(cycleId?: string): Promise<TaskParentLinkOption[]> {
  const response = await apiClient.get<TaskParentLinkOption[]>('/v1/me/tasks/parent-options', {
    params: { cycleId: cycleId || undefined },
  });
  return response.data;
}

export async function assignMyTask(taskId: string, ownerUserId: string): Promise<void> {
  await apiClient.post(`/v1/me/tasks/${taskId}/assign`, { ownerUserId });
  invalidateMyTasksCache();
}

export async function duplicateMyTask(taskId: string): Promise<void> {
  await apiClient.post(`/v1/me/tasks/${taskId}/duplicate`);
  invalidateMyTasksCache();
}

export async function replaceMyTaskAreas(taskId: string, areas: string[]): Promise<void> {
  await apiClient.put(`/v1/me/tasks/${taskId}/areas`, { areas });
  invalidateMyTasksCache();
}

export async function relinkMyTask(
  taskId: string,
  payload: { parentWorkItemId?: string; objectiveId?: string },
): Promise<void> {
  await apiClient.post(`/v1/me/tasks/${taskId}/relink`, payload);
  invalidateMyTasksCache();
}

function toEvidenceFormData(payload: PlanningWorkItemEvidenceFormPayload): FormData {
  const formData = new FormData();
  formData.append('title', payload.title);
  formData.append('description', payload.description);
  if (payload.externalUrl) formData.append('externalUrl', payload.externalUrl);
  if (payload.removeImage) formData.append('removeImage', 'true');
  if (payload.image) formData.append('image', payload.image);
  return formData;
}

export async function listMyTaskEvidences(taskId: string): Promise<PlanningWorkItemEvidence[]> {
  const response = await apiClient.get<PlanningWorkItemEvidence[]>(
    `/v1/me/tasks/${taskId}/evidences`,
  );
  return response.data;
}

export async function addMyTaskEvidence(
  taskId: string,
  payload: PlanningWorkItemEvidenceFormPayload,
): Promise<PlanningWorkItemEvidence> {
  const response = await apiClient.post<PlanningWorkItemEvidence>(
    `/v1/me/tasks/${taskId}/evidences`,
    toEvidenceFormData(payload),
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return response.data;
}

export async function updateMyTaskEvidence(
  taskId: string,
  evidenceId: string,
  payload: PlanningWorkItemEvidenceFormPayload,
): Promise<PlanningWorkItemEvidence> {
  const response = await apiClient.put<PlanningWorkItemEvidence>(
    `/v1/me/tasks/${taskId}/evidences/${evidenceId}`,
    toEvidenceFormData(payload),
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return response.data;
}

export async function deleteMyTaskEvidence(taskId: string, evidenceId: string): Promise<void> {
  await apiClient.delete(`/v1/me/tasks/${taskId}/evidences/${evidenceId}`);
}

export async function fetchMyTaskEvidenceImageUrl(
  taskId: string,
  evidenceId: string,
): Promise<{ url: string }> {
  const response = await apiClient.get<{ url: string }>(
    `/v1/me/tasks/${taskId}/evidences/${evidenceId}/image-url`,
  );
  return response.data;
}
